import * as React from 'react'
import { useCallback, useRef, useState, useEffect } from 'react'
import {
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Radio,
  RadioGroup,
  TextField,
  Typography,
} from '@material-ui/core'
import { t } from 'ttag'
import {
  isPasswordFieldErrorVisible,
  isSelectFieldErrorVisible,
  isTextFieldErrorVisible,
} from '../../../common/helpers/formHelpers'
import axios from 'axios'
import dayjs from '../../../dayjsConfig'
import { yuMuuvPublicCompanyId } from '../../Settings/Settings'
import {
  CompanyCountriesDto,
  CompanyId,
  DivisionDto,
  DivisionCategoryRow,
  DivisionId,
  ServiceProvider,
  Units,
  UserCompanyDto,
  UserProfileDto,
} from '../../../services/api.types'
import ErrorMessage from '../../../components/ErrorMessage/ErrorMessage'
import YumButton from '../../../components/YumButton/YumButton'
import YumCountrySelect from '../../../components/YumCountrySelect/YumCountrySelect'
import YumInput from '../../../components/YumInput/YumInput'
import YumPhoneInput from '../../../components/YumPhoneInput/YumPhoneInput'
import YumTimeZoneSelect from '../../../components/YumTimeZoneSelect/YumTimeZoneSelect'
import TeamSelectionUI from '../../../components/TeamSelectionUI/TeamSelectionUI'
import GroupSelectionUI from '../../../components/GroupSelectionUI/GroupSelectionUI'
import { hasFeatureFlag } from '../../../common/helpers/featureFlags'
import { ReactComponent as CheckboxIcon } from '../../../assets/v3/icons/checkbox.svg'
import { ReactComponent as CheckboxSelectedIcon } from '../../../assets/v3/icons/checkbox-selected.svg'
import {
  isCompanyCountryRequired,
  isCompanyDivisionRequired,
  isCompanyTeamSelectionLocked,
  isCompanyUserCreatedTeams,
  getCompanyMaxTeamSize,
} from '../RegisterUser'

export interface ProfileFormProps {
  // Props from parent
  userDto: UserProfileDto
  userCompanies: UserCompanyDto[]
  countries: CompanyCountriesDto[]
  divisions: DivisionDto[]
  divisionCategories: DivisionCategoryRow[]
  companyId: CompanyId
  hasSingleCompany: boolean
  createdDivisionId: DivisionId
  lastUpdate: number | null

  // State setters (for parent coordination)
  setIsSubmitInProgress: (value: boolean) => void

  // Handlers
  registerUser: (payload: any) => void
  onUnitsChange: (units: Units.METRIC | Units.IMPERIAL) => void
}

const timeZoneGuess = dayjs.tz.guess()

const getDefaultCountry = async () => {
  try {
    const response = await axios.get('https://ipv4-check-perf.radar.cloudflare.com/api/info')
    return response.data.country
  } catch (error) {
    console.error('Error fetching country:', error)
  }
  return ''
}

export const ProfileForm: React.FC<ProfileFormProps> = ({
  userDto,
  userCompanies,
  countries,
  divisions,
  divisionCategories,
  companyId,
  hasSingleCompany,
  createdDivisionId,
  lastUpdate,
  setIsSubmitInProgress,
  registerUser,
  onUnitsChange,
}) => {
  const { serviceProvider, passwordSet, googleId, appleId } = userDto

  const isUsa = timeZoneGuess.startsWith('America/') || timeZoneGuess.startsWith('US/')
  const [units, setUnits] = useState<Units.METRIC | Units.IMPERIAL>(isUsa ? Units.IMPERIAL : Units.METRIC)

  // Form state - moved from parent component
  const [firstName, setFirstName] = useState<string>('')
  const [lastName, setLastName] = useState<string>('')
  const [password, setPassword] = useState<string>('')
  const [country, setCountry] = useState<string>('')
  const [timeZone, setTimeZone] = useState<string>(timeZoneGuess)
  const [divisionId, setDivisionId] = useState<DivisionId>('')
  const [phone, setPhone] = useState<string>('')
  const [ssn, setSsn] = useState<string>('')
  const [selectedGroupIdsPerCategory, setSelectedGroupIdsPerCategory] = useState<{
    [categoryId: string]: DivisionId | null
  }>({})

  // Internal state for form validation
  const [profileFormValid, setProfileFormValid] = useState<boolean>(true)
  const [isSubmitInProgress, setIsSubmitInProgressLocal] = useState<boolean>(false)

  // Wraps the form so we can find the first invalid control after a failed submit.
  const formRef = useRef<HTMLDivElement>(null)

  // Move focus to the first invalid field so keyboard / screen-reader users land on
  // it after a failed submit (WCAG 2.4.3 Focus Order, 3.3.1 Error Identification).
  // The ErrorMessage alerts handle the announcement. Works across the form's mix of
  // text inputs, native <select>s and autocomplete inputs by targeting whatever
  // focusable control sits inside an errored (.Mui-error) field, in DOM order.
  const focusFirstInvalid = useCallback(() => {
    const root = formRef.current
    if (!root) return
    const candidates = root.querySelectorAll<HTMLElement>(
      '.Mui-error input, .Mui-error select, .Mui-error textarea, .Mui-error [role="button"], [aria-invalid="true"]',
    )
    const target = Array.from(candidates).find((el) => el.offsetParent !== null && el.getAttribute('type') !== 'hidden')
    if (target) {
      target.focus()
      target.scrollIntoView({ block: 'center', behavior: 'smooth' })
    }
  }, [])

  // Handle input changes for firstName, lastName, password
  const handleInputChange = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target
    if (name === 'firstName') setFirstName(value)
    if (name === 'lastName') setLastName(value)
    if (name === 'password') setPassword(value)
    if (name === 'phone') setPhone(value)
    if (name === 'ssn') setSsn(value)
  }, [])

  const handleUnitsChange = useCallback(
    (event: any, unitsValue: any) => {
      setUnits(unitsValue)
      onUnitsChange(unitsValue)
    },
    [onUnitsChange],
  )

  //   // TODO: We can't use this, issue with team/group selection.
  //   // Handle createdDivisionId prop - set divisionId when created
  //   useEffect(() => {
  //     if (createdDivisionId) {
  //       setDivisionId(createdDivisionId)
  //     }
  //   }, [createdDivisionId])

  // Handle default country detection
  useEffect(() => {
    const uc = userCompanies.filter((uc: { companyId: string }) => uc.companyId !== yuMuuvPublicCompanyId)
    const detectDefaultCountry = async () => {
      if (userDto.onboardingStatus === 'PROFILE_DATA' && !uc[0]?.country && countries?.length > 0 && !country) {
        const companyCountries = countries.find((c) => c.companyId === companyId)
        if (companyCountries) {
          const defaultCountry = await getDefaultCountry()
          if (defaultCountry) {
            const countryExists = companyCountries.countries.includes(defaultCountry)
            if (countryExists) {
              setCountry(defaultCountry)
            }
          }
        }
      }
    }
    detectDefaultCountry()
  }, [userDto.onboardingStatus, userCompanies, countries, country, companyId])

  // Handle userDto serviceProvider prop change - populate form data from integration (profile fields only)
  useEffect(() => {
    // const { serviceProvider: userServiceProvider } = userDto
    const uc = userCompanies.filter((uc: { companyId: string }) => uc.companyId !== yuMuuvPublicCompanyId)

    // if (userServiceProvider !== ServiceProvider.APPLE_HEALTH) {
    const hasSingleCompanyValue = uc && uc.length === 1
    if (hasSingleCompanyValue && uc[0]) {
      const { divisionId: userDivisionId, country: userCountry, divisionIds: userDivisionIds } = uc[0]
      setDivisionId(userDivisionId || null)
      setCountry(userCountry || '')

      // Parse and set selected group IDs per category from divisionIds JSON string
      // divisionIds is a JSON string with structure: {"categoryId": "divisionId"}
      if (userDivisionIds && divisionCategories) {
        try {
          // Parse the JSON string to get the mapping of categoryId -> divisionId
          const divisionIdsObj = JSON.parse(userDivisionIds)

          // Build the selectedGroupIdsPerCategory object for categories in the current company
          const groupIdsPerCategory: { [categoryId: string]: DivisionId | null } = {}

          // Iterate through all division categories for the current company
          divisionCategories
            .filter((category) => category.companyId === companyId)
            .forEach((category) => {
              // Get the division ID for this category from the parsed object
              groupIdsPerCategory[category.id] = divisionIdsObj[category.id] || null
            })

          // Set the state with all the group IDs per category
          setSelectedGroupIdsPerCategory(groupIdsPerCategory)
        } catch (e) {
          // If parsing fails, log a warning and set empty state
          console.warn('Failed to parse divisionIds:', userDivisionIds)
          setSelectedGroupIdsPerCategory({})
        }
      } else {
        // If no divisionIds or divisionCategories, set empty state
        setSelectedGroupIdsPerCategory({})
      }
    }
    // }
  }, [userCompanies, userDto, divisionCategories, companyId /*, userDto.serviceProvider*/])

  useEffect(() => {
    if (userDto.firstName) setFirstName(userDto.firstName)
  }, [userDto.firstName])

  useEffect(() => {
    if (userDto.lastName) setLastName(userDto.lastName)
  }, [userDto.lastName])

  useEffect(() => {
    if (userDto.timeZone) setTimeZone(userDto.timeZone)
  }, [userDto.timeZone])

  useEffect(() => {
    if (userDto.phone) setPhone(userDto.phone)
  }, [userDto.phone])

  useEffect(() => {
    if (userDto.ssn) setSsn(userDto.ssn)
  }, [userDto.ssn])

  // Clear local loading state when lastUpdate changes (success or error)
  // This ensures the loader stops spinning even when errors occur
  useEffect(() => {
    if (lastUpdate !== undefined && lastUpdate !== null) {
      setIsSubmitInProgressLocal(false)
    }
  }, [lastUpdate])

  // Validation function - checks if profile form is valid
  const isProfileFormValid = useCallback(() => {
    // Teams only (category groups are validated separately below). Must match the teams-only
    // list TeamSelectionUI renders — otherwise category groups get counted as teams, so a
    // required division is flagged invalid while the team select is hidden (no teams and the
    // user can't create one), leaving the user stuck on the registration page.
    const companyDivisions =
      (divisions && divisions.filter((division) => division.categoryId === null && division.companyId === companyId)) ||
      []
    const companyCountries = countries && countries.find((country) => country.companyId === companyId)
    const isTeamSelectionLocked = isCompanyTeamSelectionLocked(companyId)
    const requirePhoneField = hasFeatureFlag(userCompanies, 'phoneRequired')
    const requireSsnField = hasFeatureFlag(userCompanies, 'ssnRequired')
    const showPhoneField = hasFeatureFlag(userCompanies, 'phoneField')
    const showSsnField = hasFeatureFlag(userCompanies, 'ssnField')
    const ssnLastFour = hasFeatureFlag(userCompanies, 'ssnLastFour')

    // There might be no teams, but team is required - user should create one.
    const moreThanOneTeam = companyDivisions && companyDivisions.length
    const isUserCreatedTeams = isCompanyUserCreatedTeams(companyId) && !isTeamSelectionLocked
    const isDivisionRequired =
      !isTeamSelectionLocked &&
      (moreThanOneTeam || isUserCreatedTeams) &&
      isCompanyDivisionRequired(companyId, companyDivisions)

    const isCountryRequired =
      companyCountries && companyCountries.countries.length && isCompanyCountryRequired(companyId)
    const isDivisionInvalid = isDivisionRequired && !divisionId
    const isCountryInvalid = isCountryRequired && !country

    const cleanedPhone = phone ? phone.replace('+', '').trim() : ''
    const isPhoneInvalid = requirePhoneField && showPhoneField ? !cleanedPhone : false
    // When ssnLastFour is enabled, only 4 digits are valid.
    const isSsnInvalid = requireSsnField && showSsnField ? (ssnLastFour ? !/^\d{4}$/.test(ssn || '') : !ssn) : false

    // Check if any required category group is missing.
    // Only enforce the requirement when the user can actually provide a selection, mirroring
    // GroupSelectionUI's visibility: it hides the select entirely when there are no groups to
    // pick from and the user can't create one (or selection is admin-locked). Requiring a group
    // the user has no way to choose would leave them stuck on the registration page.
    const companyCategories = divisionCategories?.filter((category) => category.companyId === companyId) || []
    const hasRequiredGroupMissing = companyCategories.some((category) => {
      if (!(category.required ?? false)) return false
      const categoryGroups = divisions?.filter((d) => d.categoryId === category.id) || []
      const selectionLocked = category.selectionLocked ?? false
      const canCreate = category.usersCanCreate ?? true
      const canProvideGroup = !selectionLocked && (categoryGroups.length > 0 || canCreate)
      return canProvideGroup && !selectedGroupIdsPerCategory[category.id]
    })

    return !(
      (
        !firstName ||
        !lastName ||
        isDivisionInvalid ||
        isCountryInvalid ||
        isPhoneInvalid ||
        isSsnInvalid ||
        hasRequiredGroupMissing
      ) /*||
      (password.trim().length < 6 &&
        serviceProvider !== ServiceProvider.APPLE_HEALTH &&
        !passwordSet &&
        !googleId &&
        !appleId)*/
    )
  }, [
    firstName,
    lastName,
    password,
    divisionId,
    country,
    phone,
    ssn,
    companyId,
    divisions,
    countries,
    divisionCategories,
    selectedGroupIdsPerCategory,
    userCompanies,
    serviceProvider,
    passwordSet,
    googleId,
    appleId,
  ])

  // Submit handler - validates and submits profile form
  const handleSubmit = useCallback(() => {
    // Build groups array from selected group IDs per category for current company.
    // `divisionCategories` can be undefined if the GET failed (e.g. 401 while
    // email verification is still in progress) — guard so Next doesn't throw.
    const groupsArray = (divisionCategories ?? [])
      .filter((category) => category.companyId === companyId)
      .map((category) => ({
        categoryId: category.id,
        divisionId: selectedGroupIdsPerCategory[category.id] || null,
      }))

    const formValid = isProfileFormValid()
    setProfileFormValid(formValid)

    if (formValid) {
      let phoneCleaned = phone?.trim()
      if (phoneCleaned === '+') phoneCleaned = ''
      setIsSubmitInProgressLocal(true)
      setIsSubmitInProgress(true)
      registerUser({
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        password,
        divisionId: divisionId !== '' ? divisionId : null,
        country,
        timeZone,
        phone: phoneCleaned || '',
        ssn: ssn || '',
        onlyProfileUpdate: !hasSingleCompany,
        divisions: groupsArray,
        units,
      })
    } else {
      // Let the error states render, then focus the first invalid field.
      setTimeout(focusFirstInvalid, 0)
    }
  }, [
    divisionCategories,
    companyId,
    selectedGroupIdsPerCategory,
    isProfileFormValid,
    focusFirstInvalid,
    firstName,
    lastName,
    password,
    divisionId,
    country,
    timeZone,
    phone,
    ssn,
    units,
    hasSingleCompany,
    registerUser,
    setIsSubmitInProgress,
  ])

  // Internal handlers - these are only used in this component
  const handleCountryChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setCountry(event.target.value as string)
    },
    [setCountry],
  )

  const handleTimeZoneChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      setTimeZone(event.target.value as string)
    },
    [setTimeZone],
  )

  const handleDivisionChange = useCallback(
    (event: React.ChangeEvent<{ value: unknown }>) => {
      const { value } = event.target as { value: string }
      setDivisionId(value || null)
    },
    [setDivisionId],
  )

  const handleCategoryGroupChange = useCallback(
    (categoryId: string) => (event: React.ChangeEvent<{ value: unknown }>) => {
      const { value } = event.target as { value: string }
      setSelectedGroupIdsPerCategory((prevState) => ({
        ...prevState,
        [categoryId]: value || null,
      }))
    },
    [setSelectedGroupIdsPerCategory],
  )

  // Filter divisions for current company (teams only, no category groups)
  const companyDivisions =
    (divisions && divisions.filter((division) => division.categoryId === null && division.companyId === companyId)) ||
    []

  // Get countries for current company
  const companyCountries = countries && countries.find((country) => country.companyId === companyId)

  // Feature flag for autocomplete select
  const useAutocompleteSelect = hasFeatureFlag(userCompanies, 'useAutocompleteSelect')

  // Company-specific settings
  const isTeamSelectionLocked = isCompanyTeamSelectionLocked(companyId)
  const isUserCreatedTeams = isCompanyUserCreatedTeams(companyId) && !isTeamSelectionLocked
  const maxTeamSize = getCompanyMaxTeamSize(companyId)
  const showPhoneField = hasFeatureFlag(userCompanies, 'phoneField')
  const showSsnField = hasFeatureFlag(userCompanies, 'ssnField')
  const showTimeZoneField = hasFeatureFlag(userCompanies, 'showTimeZoneField')
  const requirePhoneField = hasFeatureFlag(userCompanies, 'phoneRequired')
  const requireSsnField = hasFeatureFlag(userCompanies, 'ssnRequired')
  const ssnLastFour = hasFeatureFlag(userCompanies, 'ssnLastFour')
  const ssnHelperText = ssnLastFour ? t`Enter the last 4 digits` : undefined
  const isSsnInvalid =
    requireSsnField && showSsnField
      ? ssnLastFour
        ? !/^\d{4}$/.test(ssn || '') && !profileFormValid
        : isTextFieldErrorVisible(ssn, profileFormValid)
      : false
  const isPhoneInvalid =
    requirePhoneField && isTextFieldErrorVisible(phone?.replace('+', '').trim() || '', profileFormValid)

  // Determine if team selection should be shown
  const showTeamSelect = (companyDivisions && companyDivisions.length && !isTeamSelectionLocked) || isUserCreatedTeams

  // Validation flags
  const isDivisionRequired = isCompanyDivisionRequired(companyId, companyDivisions) && !isTeamSelectionLocked
  const isCountryRequired = isCompanyCountryRequired(companyId)

  return (
    <div ref={formRef} style={{ display: 'contents' }}>
      <Typography variant="subtitle2" style={{ marginBottom: '24px' }}>
        {t`Edit profile`}
      </Typography>
      {/* First Name Field */}
      <div style={{ marginBottom: '16px' }}>
        <TextField
          variant="filled"
          InputProps={{
            disableUnderline: true,
            autoComplete: 'given-name',
          }}
          value={firstName || ''}
          label={t`First name`}
          fullWidth={true}
          type="text"
          name="firstName"
          required={true}
          error={isTextFieldErrorVisible(firstName, profileFormValid)}
          style={{
            marginBottom: '0px',
          }}
          onChange={handleInputChange}
        />
        {isTextFieldErrorVisible(firstName, profileFormValid) && (
          <ErrorMessage>{t`Enter your first name.`}</ErrorMessage>
        )}
      </div>
      {/* Last Name Field */}
      <div style={{ marginBottom: '16px' }}>
        <TextField
          variant="filled"
          InputProps={{
            disableUnderline: true,
            autoComplete: 'family-name',
          }}
          value={lastName || ''}
          label={t`Last name`}
          fullWidth={true}
          type="text"
          name="lastName"
          required={true}
          error={isTextFieldErrorVisible(lastName, profileFormValid)}
          style={{
            marginBottom: '0px',
          }}
          onChange={handleInputChange}
        />
        {isTextFieldErrorVisible(lastName, profileFormValid) && <ErrorMessage>{t`Enter your last name.`}</ErrorMessage>}
      </div>
      {/* Optional phone and SSN fields controlled by feature flags */}
      {showSsnField && (
        <div style={{ marginBottom: '16px' }}>
          <YumInput
            label={ssnLastFour ? t`Last 4 digits of your SSN` : t`Social security number`}
            type="text"
            name="ssn"
            value={ssn || ''}
            required={requireSsnField}
            //helperText={!isSsnInvalid ? ssnHelperText : undefined}
            error={isSsnInvalid}
            inputProps={ssnLastFour ? { inputMode: 'numeric', maxLength: 4 } : undefined}
            onChange={handleInputChange}
            style={{ marginBottom: 0 }}
          />
          {isSsnInvalid && (
            <ErrorMessage>
              {ssnLastFour
                ? t`Enter the last 4 digits of your social security number.`
                : t`Enter your social security number.`}
            </ErrorMessage>
          )}{' '}
        </div>
      )}
      {showPhoneField && (
        <div style={{ marginBottom: '16px' }}>
          <YumPhoneInput
            label={t`Phone number`}
            name="phone"
            value={phone || ''}
            onChange={handleInputChange}
            required={requirePhoneField}
            error={isPhoneInvalid}
            style={{ marginBottom: 0 }}
          />
          {isPhoneInvalid && <ErrorMessage>{t`Enter your phone number.`}</ErrorMessage>}
        </div>
      )}
      {/* Team Selection - Only show if single company and teams available */}
      {hasSingleCompany && (showTeamSelect || isUserCreatedTeams) ? (
        <>
          {showTeamSelect ? (
            <TeamSelectionUI
              companyId={companyId}
              userId={userDto?.userId || ''}
              divisionId={divisionId}
              divisions={companyDivisions}
              teamSelectionLocked={false}
              maxTeamSize={maxTeamSize}
              error={isDivisionRequired && isSelectFieldErrorVisible(divisionId, profileFormValid)}
              errorMessage={t`Team selection is required.`}
              showIcon={true}
              useAutocompleteSelect={useAutocompleteSelect}
              onDivisionChange={(companyId, userId) => (event) => {
                handleDivisionChange(event)
              }}
              isDuringRegistration={true}
            />
          ) : (
            ''
          )}
        </>
      ) : (
        ''
      )}
      {/* Division Category Groups - Show individual select boxes for each category */}
      {hasSingleCompany &&
        divisionCategories &&
        divisionCategories
          .filter((category) => category.companyId === companyId)
          .map((category) => {
            const categoryGroups = (divisions && divisions.filter((d) => d.categoryId === category.id)) || []
            const selectedGroupId = selectedGroupIdsPerCategory?.[category.id] || null
            const isGroupSelectionRequired = category.required ?? false
            const showGroupError =
              isGroupSelectionRequired && isSelectFieldErrorVisible(selectedGroupId, profileFormValid)

            return (
              <div key={category.id} style={{ marginBottom: '16px' }}>
                <GroupSelectionUI
                  companyId={companyId}
                  userId={userDto?.userId || ''}
                  category={category}
                  groups={categoryGroups}
                  selectedGroupId={selectedGroupId}
                  onGroupChange={handleCategoryGroupChange}
                  showIcon={true}
                  useAutocompleteSelect={useAutocompleteSelect}
                  isDuringRegistration={true}
                  error={showGroupError}
                  errorMessage={t`Group selection is required.`}
                />
              </div>
            )
          })}
      {/* Country Selection */}
      {companyCountries && companyCountries.countries.length ? (
        <FormControl style={{ marginBottom: '16px', display: 'block' }}>
          <YumCountrySelect
            label={t`Country`}
            countries={companyCountries.countries}
            value={country}
            onChange={handleCountryChange}
            error={isCountryRequired && isSelectFieldErrorVisible(country, profileFormValid)}
            showIcon={true}
            useAutocompleteSelect={useAutocompleteSelect}
            style={{
              marginBottom: 0,
            }}
          />
          {isCountryRequired && isSelectFieldErrorVisible(country, profileFormValid) && (
            <ErrorMessage>{t`Country selection is required.`}</ErrorMessage>
          )}
        </FormControl>
      ) : (
        ''
      )}
      {/* Time Zone Selection */}
      {showTimeZoneField && (
        <FormControl style={{ marginBottom: '16px', display: 'block' }}>
          <YumTimeZoneSelect
            label={t`Time zone`}
            value={timeZone}
            onChange={handleTimeZoneChange}
            required={true}
            useAutocompleteSelect={useAutocompleteSelect}
            style={{
              marginBottom: 0,
            }}
          />
        </FormControl>
      )}
      {/* Password Field - Only show if not using Apple Health and no password set */}
      {/* {serviceProvider !== ServiceProvider.APPLE_HEALTH && !passwordSet && !googleId && !appleId ? (
        <>
          <TextField
            variant="filled"
            label={t`Add password`}
            type="password"
            name="password"
            required={true}
            error={isPasswordFieldErrorVisible(password, profileFormValid)}
            style={{
              marginBottom: '8px',
            }}
            onChange={handleInputChange}
          />
          {isPasswordFieldErrorVisible(password, profileFormValid) && (
            <ErrorMessage>{t`Password must be at least 6 characters`}</ErrorMessage>
          )}
        </>
      ) : null} */}
      {/* Units Selection */}
      <FormGroup row={true}>
        <FormControl component="fieldset" style={{ margin: '0 10px 12px 10px' }}>
          <FormLabel component="legend" style={{ fontSize: '0.75rem' }}>
            {t`Units of measure`}
          </FormLabel>
          <RadioGroup
            row
            aria-label={t`Units of measure`}
            name="units"
            onChange={handleUnitsChange}
            value={units || Units.METRIC}
          >
            <FormControlLabel
              control={<Radio icon={<CheckboxIcon />} checkedIcon={<CheckboxSelectedIcon />} />}
              value={Units.METRIC}
              label={t`Metric`}
            />
            <FormControlLabel
              control={<Radio icon={<CheckboxIcon />} checkedIcon={<CheckboxSelectedIcon />} />}
              value={Units.IMPERIAL}
              label={t`Imperial`}
            />
          </RadioGroup>
        </FormControl>
      </FormGroup>
      {/* Submit Button */}
      <YumButton showLoader={true} disabled={isSubmitInProgress} onClick={handleSubmit}>
        {t`Next`}
      </YumButton>
    </div>
  )
}
